import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/client/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-full border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90',
        destructive:
          'border-transparent bg-destructive text-destructive-foreground [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20',
        outline: 'text-foreground [a&]:hover:bg-muted',
        accent: 'border-transparent bg-accent text-accent-foreground [a&]:hover:bg-accent/90',
        success: 'border-transparent bg-success text-success-foreground [a&]:hover:bg-success/90',
        // Subtle variants for softer emphasis
        'primary-subtle': 'border-transparent bg-primary/10 text-primary [a&]:hover:bg-primary/20',
        'accent-subtle': 'border-transparent bg-accent/10 text-accent [a&]:hover:bg-accent/20',
        'success-subtle': 'border-transparent bg-success/10 text-success [a&]:hover:bg-success/20',
        'destructive-subtle':
          'border-transparent bg-destructive/10 text-destructive [a&]:hover:bg-destructive/20',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'span';

  return (
    <Comp data-slot="badge" className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

export { Badge, badgeVariants };
